import { StrictMode, Component, ErrorInfo, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { initSentry, Sentry } from './lib/sentry';
import { isChunkLoadError } from './lib/lazyRoute';
import { registerDeployRecoveryListeners, reloadForStaleDeploy } from './lib/deployRecovery';

registerDeployRecoveryListeners();
import App from './App.tsx';
import './index.css';
import { initializePlatform } from './hooks/usePlatform';

initSentry();

// Error boundary to catch React rendering errors
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, backgroundColor: '#fee2e2', minHeight: '100vh' }}>
          <h1 style={{ color: '#dc2626', marginBottom: 10 }}>App Error</h1>
          <p style={{ color: '#991b1b', marginBottom: 10 }}>{this.state.error?.message}</p>
          <pre style={{ fontSize: 12, overflow: 'auto', backgroundColor: '#fef2f2', padding: 10, borderRadius: 8 }}>
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auto-reload on stale chunk errors (e.g. after a new deployment while a tab is open)
window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    reloadForStaleDeploy();
  } else {
    Sentry.captureException(event.reason);
  }
});


// Initialize platform-specific features when app loads
// Add a timeout to ensure the app renders even if platform init hangs
const platformInitPromise = Promise.race([
  initializePlatform(),
  new Promise((resolve) => setTimeout(resolve, 3000)) // 3 second timeout
]).catch((error) => {
  console.warn('Platform initialization error:', error);
});


// Render the app immediately - don't wait for platform init
try {
  const root = document.getElementById('root');
  
  createRoot(root!).render(
    <StrictMode>
      <HelmetProvider>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </HelmetProvider>
    </StrictMode>
  );
} catch (error) {
  console.error('Error during React mount:', error);
  document.body.innerHTML = `<div style="padding:20px;color:red;"><h1>Mount Error</h1><pre>${error}</pre></div>`;
}

platformInitPromise.then(() => {});
